import React from 'react';
import './Onboarding.css';
import GuidedTourOverlay from './guidedTour/GuidedTourOverlay';
import type { GuidedTourStep } from './guidedTour/types';
import { useGuidedTour } from '../hooks/useGuidedTour';

export type ProfileOnboardingTab = 'profile' | 'history' | 'usage' | 'models' | 'fine-tuning';

interface ProfileOnboardingStep extends GuidedTourStep {
  tab?: ProfileOnboardingTab;
}

interface ProfileOnboardingProps {
  isOpen: boolean;
  onClose: () => void;
  onTabChange?: (tab: ProfileOnboardingTab) => void;
}

const PROFILE_ONBOARDING_STEPS: ProfileOnboardingStep[] = [
  {
    id: 'profile-onboarding-toolbar',
    title: 'Account page & Help',
    contextLabel: 'Top bar — Help',
    description:
      'This screen is your personal workspace: personal data, past optimizations, API usage, which fine-tuned models appear in Connector, and fine-tuning jobs. Use Help any time to replay this tour. The sections below explain each block step by step.',
    position: 'left',
    offset: { x: -24, y: 0 },
    tab: 'profile',
  },
  {
    id: 'profile-sidebar',
    title: 'Sidebar navigation',
    contextLabel: 'Left — section tabs',
    description:
      'Profile — your display name, optional company, marketing opt-in, and Save. History — every optimization run (Connector integrations vs Multi-Agent), with steps, configs, and “load back” actions. Usage — billed token history with filters and row details. Models — per integration, choose which trained models show up in the Connector model picker. Fine-tuning — monitor jobs started from Connector (search, filters, open details, load finished models).',
    position: 'right',
    offset: { x: 36, y: 0 },
    tab: 'profile',
  },
  {
    id: 'profile-onboarding-identity',
    title: 'Name & company',
    contextLabel: 'Profile card — name & company fields',
    description:
      'Name is required and how you appear in the product. It is validated for length before save. Company is optional and only for your records or support context. Character counters show limits so you can fix input before submitting.',
    position: 'right',
    offset: { x: 28, y: 0 },
    tab: 'profile',
  },
  {
    id: 'profile-onboarding-preferences',
    title: 'Communications',
    contextLabel: 'Profile card — email opt-in',
    description:
      'This checkbox is your explicit consent to receive product or marketing emails from us. It is independent of account notifications (errors, billing, security). You can change it any time and press Save to persist.',
    position: 'right',
    offset: { x: 28, y: 0 },
    tab: 'profile',
  },
  {
    id: 'profile-onboarding-save',
    title: 'Saving your profile',
    contextLabel: 'Profile card — Save button',
    description:
      'Save sends your changes to the server. While saving, the button shows a loading state and fields are disabled. If something is wrong (for example an empty name), you will see an inline error near the field; success messages appear next to the button when appropriate.',
    position: 'top',
    offset: { x: 0, y: -20 },
    tab: 'profile',
  },
  {
    id: 'profile-history-section',
    title: 'Optimization history',
    contextLabel: 'History card — whole block',
    description:
      'Each row is one optimization experiment: initial configuration, iteration steps, scores where available, and timestamps. Use it to compare runs, audit what was deployed, or recover a good configuration after you changed Connector.',
    position: 'left',
    offset: { x: -28, y: 0 },
    tab: 'history',
  },
  {
    id: 'profile-onboarding-history-categories',
    title: 'Integrations vs Multi-Agent',
    contextLabel: 'History card — category tabs',
    description:
      'Integrations lists runs tied to a Connector integration (single- or multi-agent configs saved against that integration). Multi-Agent lists runs from the standalone Multi-Agent optimizer (no integration id). The actions on a row differ: integrations often offer “Load to Connector”; multi-agent may open steps or load into the Multi-Agent page.',
    position: 'top',
    offset: { x: 0, y: -22 },
    tab: 'history',
  },
  {
    id: 'profile-onboarding-history-table',
    title: 'Table, steps, and reload',
    contextLabel: 'History card — runs list (table or cards)',
    description:
      'Runs appear as a wide table on large screens or as stacked cards on smaller ones. Columns summarize configuration label, optimization steps, score (integrations), date, and actions. Open / View steps walks through each iteration’s prompts and metrics. Where supported, Load pushes the last good step into Connector or Multi-Agent so you can continue editing or re-run from that point.',
    position: 'top',
    offset: { x: 0, y: -24 },
    tab: 'history',
  },
  {
    id: 'profile-usage-section',
    title: 'Usage & billing context',
    contextLabel: 'Usage card — totals, filters, table',
    description:
      'Usage History aggregates token consumption from product features (optimization, chat, fine-tuning, evaluation, etc.). Totals summarize prompt, completion, cached tokens, and counts. Filters restrict by activity type and date range; Refresh reloads the current page. Click a row to open a detail drawer with the underlying request or job metadata when the API provides it.',
    position: 'left',
    offset: { x: -28, y: 0 },
    tab: 'usage',
  },
  {
    id: 'profile-models-section',
    title: 'Models in Connector',
    contextLabel: 'Models card — title & intro',
    description:
      'Fine-tuning produces new model IDs. Here you decide which of those models appear in the Connector configurator when you pick a model for an agent. Hiding a model does not delete it from the provider — it only controls the in-app picker for that integration.',
    position: 'right',
    offset: { x: 28, y: 0 },
    tab: 'models',
  },
  {
    id: 'profile-onboarding-models-integration',
    title: 'Choose an integration',
    contextLabel: 'Models card — integration picker',
    description:
      'Configurator visibility is per integration. Select the integration first; the list loads trainable models discovered for that workspace. If the list is empty, run at least one fine-tuning job for that integration from Connector, then refresh this page.',
    position: 'right',
    offset: { x: 24, y: 0 },
    tab: 'models',
  },
  {
    id: 'profile-onboarding-models-toggles',
    title: 'Enable or hide models',
    contextLabel: 'Models card — model toggles',
    description:
      'Each row is a trainable model name with base model metadata. The switch adds or removes that model id from the Connector configurator allowlist for the selected integration. Changes apply immediately in the UI; use notifications as feedback. Agents already using a model are unchanged until you edit and save configuration in Connector.',
    position: 'right',
    offset: { x: 24, y: 0 },
    tab: 'models',
  },
  {
    id: 'profile-fine-tuning-section',
    title: 'Fine-tuning jobs & list',
    contextLabel: 'Fine-tuning card — header + list area',
    description:
      'Jobs started from Connector appear in this card. The header has the title and Refresh. Below: loading state, empty hint, or your jobs. With data, use search (model, integration id, job id, status), status and integration filters, and pagination text. Load more appears when more pages exist. Each row: Details for the full payload; Load to Connector (when the job succeeded) registers the fine-tuned model. Use this list to confirm completion, read errors, or copy a model id before enabling it on the Models tab.',
    position: 'right',
    offset: { x: 28, y: 0 },
    tab: 'fine-tuning',
  },
];

const ProfileOnboarding = ({ isOpen, onClose, onTabChange }: ProfileOnboardingProps) => {
  const tour = useGuidedTour({
    isOpen,
    steps: PROFILE_ONBOARDING_STEPS,
    onClose,
    layout: 'center',
    extraHighlightClasses: ['profile-onboarding-spotlight'],
    onStepEnter: (step) => {
      if (step.tab) onTabChange?.(step.tab as ProfileOnboardingTab);
    },
  });

  if (!isOpen || !tour.currentStepData) return null;

  return (
    <GuidedTourOverlay
      isOpen={isOpen}
      tourTitle="Profile guided tour"
      stepsCount={tour.stepCount}
      currentStep={tour.safeStepIndex}
      stepTitle={tour.currentStepData.title}
      stepDescription={tour.currentStepData.description}
      contextLabel={tour.currentStepData.contextLabel}
      tooltipX={tour.tooltipPosition.x}
      tooltipY={tour.tooltipPosition.y}
      position={tour.currentPosition}
      onClose={tour.handleSkip}
      onNext={tour.handleNext}
      onPrev={tour.handlePrev}
    />
  );
};

export default ProfileOnboarding;
